"use client";

import React from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { PropertyService, PropertyLeadType } from "@/services";
import { usePostHogAnalytics } from "@/contexts/PostHogAnalyticsContext";
import { buildActionId, startUserAction } from "@/utils/idempotency";

export interface PropertyLeadContext {
  leadUserId?: string | null;
  isGuestUser?: boolean;
  realtorId?: string | null;
  propertyCreatedById?: string | null;
  leadUserRole?: string | null;
}

export interface PropertyContactContext {
  property_type?: string;
  listing_type?: string;
  location_city?: string;
  price?: number;
  realtor_id?: string;
  developer_id?: string;
}

interface PhoneCallButtonProps {
  phone: string;
  propertyId?: string;
  lead?: PropertyLeadContext;
  propertyContext?: PropertyContactContext;
  className?: string;
  buttonText?: string;
}

export const PhoneCallButton: React.FC<PhoneCallButtonProps> = ({
  phone,
  propertyId,
  lead,
  propertyContext,
  className = "w-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-semibold rounded-xl transition-colors shadow-xs",
  buttonText = "Place Direct Call",
}) => {
  const { trackDirectCallClicked } = usePostHogAnalytics();

  const handleCallClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!phone) return;


    // (no second dialer bounce).
    const idempotencyKey = startUserAction(
      buildActionId("contact", "phone", propertyId),
    );
    if (!idempotencyKey) {
      console.debug(
        "[PhoneCallButton] duplicate click skipped",
        { propertyId, action: "contact:phone" },
      );
      return;
    }

    console.debug("[PhoneCallButton] click started", {
      propertyId,
      action: "contact:phone",
      idempotencyKey,
    });

    if (propertyId) {
      console.debug("[PhoneCallButton] tracking insight", {
        propertyId,
        action: "direct_phone_call",
        idempotencyKey,
        t: Date.now(),
      });
      PropertyService.TrackPropertyInsight(propertyId, "direct_phone_call", {
        idempotencyKey,
      });
      console.debug("[PhoneCallButton] tracking lead", {
        propertyId,
        type: PropertyLeadType.DIRECT_CALL_CLICK,
        idempotencyKey,
        t: Date.now(),
      });
      /*
      
      PropertyService.TrackPropertyLead(
        {
          propertyId,
          type: PropertyLeadType.DIRECT_CALL_CLICK,
          leadUserId: lead?.leadUserId ?? null,
          isGuestUser: lead?.isGuestUser ?? true,
          realtorId: lead?.realtorId ?? null,
          propertyCreatedById: lead?.propertyCreatedById ?? null,
          leadUserRole: lead?.leadUserRole ?? null,
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent : null,
          ipAddress: null,
        },
        { idempotencyKey },
      );
      */
      trackDirectCallClicked({
        property_id: propertyId,
        listing_type: propertyContext?.listing_type,
        property_type: propertyContext?.property_type,
        location_city: propertyContext?.location_city,
        price: propertyContext?.price,
        realtor_id: propertyContext?.realtor_id ?? lead?.realtorId ?? undefined,
        developer_id: propertyContext?.developer_id,
      });
    }

    const rawPhone = phone.trim();
    const parsed = parsePhoneNumberFromString(rawPhone, "NG");

    const callUrl = parsed?.isValid()
      ? `tel:${parsed.number}`
      : `tel:${rawPhone.replace(/[^\d+]/g, "")}`;

    console.debug("[PhoneCallButton] navigating to call url", {
      callUrl,
      t: Date.now(),
    });

    window.location.href = callUrl;
  };

  return (
    <button
      type="button"
      className={`flex items-center justify-center gap-2 ${className}`}
      onClick={handleCallClick}
    >
      <svg
        className="w-5 h-5 fill-current text-white shrink-0"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
      </svg>
      <span>{buttonText}</span>
    </button>
  );
};